import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { MOTIVOS, BRAND } from "../lib/brand";
import { openExternal } from "../lib/openExternal";
import useReveal from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    nombre: "",
    telefono: "",
    email: "",
    motivo: "",
    mensaje: "",
};

const Contacto = () => {
    const leftRef = useReveal();
    const rightRef = useReveal();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const onChange = (e) => {
        setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.motivo) {
            toast.error("Selecciona un motivo de consulta.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            toast.success(
                res.data?.email_sent
                    ? "Solicitud enviada. Te contactaremos a la brevedad."
                    : "Solicitud recibida. Te contactaremos a la brevedad."
            );
            setForm(initial);
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.detail ||
                "Ocurrió un error al enviar. Intenta de nuevo o escríbenos por WhatsApp.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="contacto-section"
            className="relative py-24 lg:py-32 bg-cream-warm"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left intro */}
                    <div ref={leftRef} className="reveal lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-px bg-gold" />
                            <span className="eyebrow">Contacto</span>
                        </div>
                        <h2
                            data-testid="contacto-heading"
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy"
                        >
                            Da el <span className="italic text-navy/85">primer paso.</span>
                        </h2>

                        <p className="mt-8 text-ink/80 leading-relaxed text-[15px] sm:text-base font-light max-w-md">
                            Agenda tu consulta con el{" "}
                            <span className="font-medium text-navy">
                                Dr. Erik E. Cárdenas Hernández
                            </span>{" "}
                            y descubre si eres candidato para alguno de nuestros procedimientos o
                            para el Protocolo Punto Cero. El primer paso siempre es una
                            conversación.
                        </p>

                        <div className="mt-12 space-y-5 text-sm">
                            <a
                                data-testid="contact-whatsapp-link"
                                href={`https://wa.me/${BRAND.whatsappNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-4 text-navy hover:text-gold transition-colors group"
                            >
                                <span className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all">
                                    <Phone size={16} strokeWidth={1.5} />
                                </span>
                                <span className="font-light">
                                    WhatsApp · {BRAND.whatsappDisplay}
                                </span>
                            </a>
                            <a
                                data-testid="contact-linktree-link"
                                href={BRAND.linktree}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-4 text-navy hover:text-gold transition-colors group"
                            >
                                <span className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all">
                                    <Mail size={16} strokeWidth={1.5} />
                                </span>
                                <span className="font-light">
                                    linktr.ee/drerikcardenas
                                </span>
                            </a>
                            <div className="flex items-center gap-4 text-navy/80">
                                <span className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold">
                                    <MapPin size={16} strokeWidth={1.5} />
                                </span>
                                <span className="font-light">Ciudad de México · México</span>
                            </div>
                        </div>

                        {/* Social */}
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                data-testid="contacto-instagram"
                                href={BRAND.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                onClick={(e) => {
                                    e.preventDefault();
                                    openExternal(BRAND.instagram, BRAND.instagramHandle);
                                }}
                                className="group inline-flex items-center gap-2.5 px-4 py-2.5 border border-navy/15 hover:border-gold transition-colors"
                            >
                                <Instagram size={15} className="text-gold" strokeWidth={1.5} />
                                <span className="text-[11px] tracking-[0.18em] uppercase text-navy/70 group-hover:text-navy transition-colors">
                                    {BRAND.instagramHandle}
                                </span>
                            </a>
                            <a
                                data-testid="contacto-facebook"
                                href={BRAND.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                onClick={(e) => {
                                    e.preventDefault();
                                    openExternal(BRAND.facebook, BRAND.facebookHandle);
                                }}
                                className="group inline-flex items-center gap-2.5 px-4 py-2.5 border border-navy/15 hover:border-gold transition-colors"
                            >
                                <Facebook size={15} className="text-gold" strokeWidth={1.5} />
                                <span className="text-[11px] tracking-[0.18em] uppercase text-navy/70 group-hover:text-navy transition-colors">
                                    {BRAND.facebookHandle}
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Form */}
                    <form
                        ref={rightRef}
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="reveal lg:col-span-7 bg-white border border-navy/10 p-8 sm:p-10 lg:p-12 relative"
                    >
                        <div className="absolute top-0 left-0 right-0 h-px bg-gold" />
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="sm:col-span-2">
                                <label className="eyebrow !text-navy/50 block mb-2">
                                    Nombre completo
                                </label>
                                <input
                                    data-testid="contact-input-nombre"
                                    name="nombre"
                                    required
                                    minLength={2}
                                    value={form.nombre}
                                    onChange={onChange}
                                    placeholder="Tu nombre"
                                    className="form-input"
                                />
                            </div>
                            <div>
                                <label className="eyebrow !text-navy/50 block mb-2">
                                    Teléfono
                                </label>
                                <input
                                    data-testid="contact-input-telefono"
                                    name="telefono"
                                    required
                                    type="tel"
                                    value={form.telefono}
                                    onChange={onChange}
                                    placeholder="+52 ..."
                                    className="form-input"
                                />
                            </div>
                            <div>
                                <label className="eyebrow !text-navy/50 block mb-2">
                                    Correo electrónico
                                </label>
                                <input
                                    data-testid="contact-input-email"
                                    name="email"
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={onChange}
                                    placeholder="tu@correo.com"
                                    className="form-input"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="eyebrow !text-navy/50 block mb-2">
                                    Motivo de consulta
                                </label>
                                <select
                                    data-testid="contact-select-motivo"
                                    name="motivo"
                                    required
                                    value={form.motivo}
                                    onChange={onChange}
                                    className="form-input bg-transparent appearance-none cursor-pointer"
                                    style={{
                                        backgroundImage:
                                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23C9A84C' stroke-width='1.4'/%3E%3C/svg%3E\")",
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 6px center",
                                        paddingRight: "28px",
                                    }}
                                >
                                    <option value="" disabled>
                                        Selecciona una opción
                                    </option>
                                    {MOTIVOS.map((m) => (
                                        <option key={m} value={m}>
                                            {m}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="eyebrow !text-navy/50 block mb-2">
                                    Mensaje
                                </label>
                                <textarea
                                    data-testid="contact-input-mensaje"
                                    name="mensaje"
                                    required
                                    minLength={5}
                                    rows={4}
                                    value={form.mensaje}
                                    onChange={onChange}
                                    placeholder="Cuéntanos brevemente cómo podemos ayudarte..."
                                    className="form-input resize-none"
                                />
                            </div>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                            <p className="text-[11px] tracking-[0.2em] uppercase text-ink/45">
                                Tu información es confidencial.
                            </p>
                            <button
                                data-testid="contact-submit-button"
                                type="submit"
                                disabled={loading}
                                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-navy text-gold text-xs tracking-[0.22em] uppercase font-semibold hover:bg-navy-deep transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? "Enviando..." : "Enviar solicitud"}
                                {!loading && (
                                    <ArrowRight
                                        size={16}
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contacto;
